import { Injectable, Injector, computed, effect, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, firstValueFrom, map, of, tap } from 'rxjs';
import { ApiError } from '../../abstract/api-error';
import { ApiService } from '../../abstract/api.service';
import { MemberOnboardingProgress } from '../response-models/member-onboarding-progress.model';
import { OnboardingChecklistItem } from '../response-models/onboarding-checklist-item.model';
import { OrgOnboardingItem } from '../response-models/org-onboarding-item.model';
import { OwnerOnboardingProgress } from '../response-models/owner-onboarding-progress.model';
import { PersonalOnboardingItem } from '../response-models/personal-onboarding-item.model';
import { CustomClaimService } from '../services/custom-claim.service';
import { OrganizationContextService } from '../services/organization-context.service';
import { isRecord } from '../utils/is-record';
import {
  combinedOnboardingPercentage,
  isOnboardingFullyComplete,
  toOrgItems,
  toOwnerOnboardingProgress,
  toPersonalItems,
} from '../utils/onboarding-utils';
import { nullableBoolean } from '../utils/wire-value-utils';
import { MemberOnboardingProgressService } from './member-onboarding-progress.service';

/**
 * Onboarding state for the dashboard card and the sidebar's compact view. Combines the member's personal
 * track (read through `MemberOnboardingProgressService`) with the organization's owner track:
 * `/organizations/:id/owner-onboarding/{progress, items/:key/complete, dismiss, show, sync}`
 * (hub `user-owner-onboarding-controller.ts`).
 *
 * Merges the former `OnboardingService` and `UnifiedOnboardingService` (ADN-2469d). State is held in signals
 * and reloads whenever the organization context changes. The owner track is only fetched for an Owner or
 * Chief Supervisor; everything here logs failures instead of throwing, because onboarding is advisory.
 *
 * `CustomClaimService` is resolved lazily: it injects `AuthService`, which injects this service.
 *
 * Reference: agent-os/specs/2026-02-13-unified-onboarding-redesign/spec.md
 */
@Injectable({
  providedIn: 'root'
})
export class OnboardingService extends ApiService {
  private router = inject(Router);
  private injector = inject(Injector);
  private organizationContext = inject(OrganizationContextService);
  private memberProgressService = inject(MemberOnboardingProgressService);

  /** Raw member onboarding progress */
  readonly personalProgress = signal<MemberOnboardingProgress | null>(null);
  /** Raw owner onboarding checklist items */
  readonly orgChecklist = signal<OnboardingChecklistItem[]>([]);
  /** Whether the onboarding card has been dismissed */
  readonly orgDismissed = signal<boolean>(false);
  /** Whether the owner onboarding has a completedAt timestamp */
  readonly orgCompleted = signal<boolean>(false);
  /** Whether data is currently being fetched */
  readonly isLoading = signal<boolean>(false);
  /** Whether the current user is Owner or Chief Supervisor */
  readonly isOwnerOrChiefSupervisor = signal<boolean>(false);
  /** Whether progress data has been loaded at least once */
  readonly hasLoadedOnce = signal<boolean>(false);

  readonly personalItems = computed<PersonalOnboardingItem[]>(() => toPersonalItems(this.personalProgress()));
  readonly orgItems = computed<OrgOnboardingItem[]>(() => toOrgItems(this.orgChecklist()));
  /** Incomplete personal items (all, not just required). */
  readonly personalIncompleteCount = computed<number>(() => this.personalItems().filter(item => !item.isCompleted).length);
  /** Incomplete org items (all, not just required). */
  readonly orgIncompleteCount = computed<number>(() => this.orgItems().filter(item => !item.isCompleted).length);
  /** Completion of the required items: 4 personal, plus 2 org items for an Owner / Chief Supervisor. */
  readonly combinedPercentage = computed<number>(() =>
    combinedOnboardingPercentage(this.personalItems(), this.orgItems(), this.isOwnerOrChiefSupervisor())
  );
  readonly isDismissed = computed<boolean>(() => this.orgDismissed());
  /** Whether all required items across both tracks are complete. */
  readonly isFullyComplete = computed<boolean>(() =>
    isOnboardingFullyComplete(this.personalItems(), this.orgItems(), this.isOwnerOrChiefSupervisor())
  );

  constructor() {
    super('owner-onboarding');
    effect(() => {
      if (this.organizationContext.currentOrganizationId()) {
        void this.loadProgress();
      }
    });
  }

  /** Load both tracks in parallel. Member progress always; owner progress only for an Owner / Chief Supervisor. */
  async loadProgress(): Promise<void> {
    const orgId = this.organizationContext.currentOrganizationId();
    if (!orgId) return;

    this.isLoading.set(true);
    try {
      const isOwnerOrCS = this.injector.get(CustomClaimService).isOwnerOrChiefSupervisor();
      this.isOwnerOrChiefSupervisor.set(isOwnerOrCS);
      const [member, owner] = await Promise.all([
        this.fetchMemberProgress(orgId),
        isOwnerOrCS ? this.fetchOwnerProgress(orgId) : Promise.resolve(null),
      ]);
      if (member) {
        this.personalProgress.set(member);
      }
      if (owner) {
        this.applyOwnerProgress(owner);
      }
      this.hasLoadedOnce.set(true);
    } catch (error: unknown) {
      logFailure('Error loading onboarding progress', error);
    } finally {
      this.isLoading.set(false);
    }
  }

  /** Dismiss the onboarding card: instant in the UI, then persisted (kept dismissed even if that fails). */
  async dismiss(): Promise<void> {
    const orgId = this.organizationContext.currentOrganizationId();
    if (!orgId) return;
    this.orgDismissed.set(true);
    await this.post(orgId, 'dismiss', 'Error dismissing onboarding');
  }

  /** Restore the dismissed card and go to the dashboard; persisted in the background. */
  async restore(): Promise<void> {
    const orgId = this.organizationContext.currentOrganizationId();
    if (!orgId) return;
    this.orgDismissed.set(false);
    if (!this.router.url.startsWith('/dashboard')) {
      this.router.navigate(['/dashboard']);
    }
    await this.post(orgId, 'show', 'Error restoring onboarding');
  }

  /** Refresh all progress after the wizard modal completes. */
  async refreshAfterWizardComplete(): Promise<void> {
    await this.loadProgress();
  }

  /**
   * Have the hub recalculate owner progress from live data (e.g. after a certification upload), then reload.
   * Fire-and-forget safe: errors are logged.
   */
  async syncAndRefresh(): Promise<void> {
    const orgId = this.organizationContext.currentOrganizationId();
    if (!orgId) return;
    await this.post(orgId, 'sync', 'Error syncing onboarding progress');
    await this.loadProgress();
  }

  /**
   * Mark an owner checklist item complete (e.g. `create_company`, `add_customers`) and update the checklist.
   * Cold, like any Observable: nothing is sent until it is subscribed. Never errors; failures are logged.
   */
  completeItem(itemKey: string): Observable<void> {
    const orgId = this.organizationContext.currentOrganizationId();
    if (!orgId) {
      return of(undefined);
    }
    const opts = { prefix: ['organizations', orgId], path: ['items', itemKey, 'complete'] };
    return this.request('POST', opts, readAllRequiredComplete, {}).pipe(
      tap(allRequiredComplete => this.markItemComplete(itemKey, allRequiredComplete)),
      map(() => undefined),
      catchError((error: unknown) => {
        logFailure('Error completing onboarding item', error);
        return of(undefined);
      })
    );
  }

  /** Clear cached onboarding state (on login), so the card reloads fresh for the signed-in user. */
  resetOnboarding(): Observable<void> {
    this.personalProgress.set(null);
    this.orgChecklist.set([]);
    this.orgDismissed.set(false);
    this.orgCompleted.set(false);
    this.hasLoadedOnce.set(false);
    return of(undefined);
  }

  private async fetchMemberProgress(orgId: string): Promise<MemberOnboardingProgress | null> {
    try {
      return await this.memberProgressService.getProgress(orgId);
    } catch (error: unknown) {
      logFailure('Error fetching member onboarding progress', error);
      return null;
    }
  }

  /** Owner progress; a 403 (not an owner after all) is expected and yields `null`. */
  private async fetchOwnerProgress(orgId: string): Promise<OwnerOnboardingProgress | null> {
    try {
      return await firstValueFrom(
        this.request('GET', { prefix: ['organizations', orgId], path: 'progress' }, toOwnerOnboardingProgress)
      );
    } catch (error: unknown) {
      if (ApiError.fromHttpError(error).status !== 403) {
        logFailure('Error fetching owner onboarding progress', error);
      }
      return null;
    }
  }

  private applyOwnerProgress(owner: OwnerOnboardingProgress): void {
    this.orgChecklist.set(owner.checklist);
    this.orgDismissed.set(owner.dismissed);
    this.orgCompleted.set(owner.completedAt !== null);
  }

  private markItemComplete(itemKey: string, allRequiredComplete: boolean): void {
    this.orgChecklist.update(items =>
      items.map(item => (item.itemKey === itemKey ? { ...item, isCompleted: true, completedAt: new Date().toISOString() } : item))
    );
    if (allRequiredComplete) {
      this.orgCompleted.set(true);
    }
  }

  /** POST an owner-onboarding action, logging (not throwing) a failure. */
  private async post(orgId: string, action: string, failure: string): Promise<void> {
    try {
      await firstValueFrom(this.request('POST', { prefix: ['organizations', orgId], path: action }, () => undefined, {}));
    } catch (error: unknown) {
      logFailure(failure, error);
    }
  }
}

function readAllRequiredComplete(data: unknown): boolean {
  return isRecord(data) && nullableBoolean(data['allRequiredComplete']) === true;
}

function logFailure(message: string, error: unknown): void {
  console.error(`[OnboardingService] ${message}:`, ApiError.fromHttpError(error).message);
}
